import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { isViteFlagEnabled } from '../../utils/runtimePlatformFlags';
import { getAuthUrl, getNavigationUrl } from '../../utils/subdomainUtils';
import { getProviderPricingSummary } from '../../../shared/providerAcquisition/pricingCopy';
import { buildVendorJoinHref } from '../../../shared/vendorDirectory/vendorDirectoryPaths';
import VendorDirectoryLaunchPricing from '../vendors/VendorDirectoryLaunchPricing';

export type SignupIntentKey = 'attendee' | 'agency' | 'employer' | 'professional' | 'vendor';

export type SignupIntentChoicesProps = {
  extraParams?: Record<string, string>;
  includeRedirect?: boolean;
  redirectUrl?: string | null;
  highlightedIntent?: SignupIntentKey | null;
  onChosen?: () => void;
};

function navigateTo(url: string): void {
  window.location.href = url;
}

export function SignupIntentChoices({
  extraParams,
  includeRedirect = false,
  redirectUrl = null,
  highlightedIntent = null,
  onChosen,
}: SignupIntentChoicesProps): React.ReactElement {
  const professionalSignupEnabled =
    import.meta.env.VITE_PROFESSIONAL_SIGNUP_ENABLED === 'true';
  const vendorDirectoryEnabled = isViteFlagEnabled(
    'VITE_VENDOR_DIRECTORY_ENABLED',
    import.meta.env.VITE_VENDOR_DIRECTORY_ENABLED,
  );

  const authOpts = {
    includeRedirect: includeRedirect || Boolean(redirectUrl),
    ...(redirectUrl ? { redirectUrl } : {}),
    extraParams,
  };

  const go = (url: string) => {
    onChosen?.();
    navigateTo(url);
  };

  const titleRow = (intent: SignupIntentKey, label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
      <Typography variant="subtitle1" component="h2" fontWeight={700}>
        {label}
      </Typography>
      {highlightedIntent === intent ? (
        <Chip label="Suggested for you" color="primary" size="small" />
      ) : null}
    </Box>
  );

  return (
    <Stack spacing={1.5}>
      <Card variant="outlined" sx={highlightedIntent === 'attendee' ? { borderColor: 'primary.main' } : undefined}>
        <CardActionArea onClick={() => go(getAuthUrl('/signup', authOpts))}>
          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', textAlign: 'left' }}>
            <PersonIcon color="primary" sx={{ fontSize: 36, mt: 0.25 }} aria-hidden />
            <Box>
              {titleRow('attendee', 'Attendee / Officer')}
              <Typography variant="body2" color="text.secondary">
                Register for training events and manage your attendee profile.
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
      <Card variant="outlined" sx={highlightedIntent === 'agency' ? { borderColor: 'primary.main' } : undefined}>
        <CardActionArea onClick={() => go(getAuthUrl('/provider-signup', authOpts))}>
          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', textAlign: 'left' }}>
            <BusinessIcon color="primary" sx={{ fontSize: 36, mt: 0.25 }} aria-hidden />
            <Box>
              {titleRow('agency', 'Agency / Coordinator')}
              <Typography variant="body2" color="text.secondary">
                Apply as a training provider to post and manage training events.{' '}
                {getProviderPricingSummary()}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
      <Card variant="outlined" sx={highlightedIntent === 'employer' ? { borderColor: 'primary.main' } : undefined}>
        <CardActionArea onClick={() => go(getAuthUrl('/employer-signup', authOpts))}>
          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', textAlign: 'left' }}>
            <WorkIcon color="primary" sx={{ fontSize: 36, mt: 0.25 }} aria-hidden />
            <Box>
              {titleRow('employer', 'Employer')}
              <Typography variant="body2" color="text.secondary">
                Create your login and register your organization in one step to post and manage
                secondary (off-duty) jobs.
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
      {vendorDirectoryEnabled ? (
        <Card variant="outlined">
          <CardActionArea
            onClick={() => {
              go(getNavigationUrl(buildVendorJoinHref({ search: extraParams })));
            }}
          >
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', textAlign: 'left' }}>
              <StoreIcon color="primary" sx={{ fontSize: 36, mt: 0.25 }} aria-hidden />
              <Box>
                <Typography variant="subtitle1" component="h2" fontWeight={700}>
                  Vendor / Supplier
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  List your company in the law enforcement vendor directory.
                </Typography>
                <VendorDirectoryLaunchPricing variant="compact" />
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      ) : null}
      {professionalSignupEnabled ? (
        <Card
          variant="outlined"
          sx={highlightedIntent === 'professional' ? { borderColor: 'primary.main' } : undefined}
        >
          <CardActionArea onClick={() => go(getAuthUrl('/professional-signup', authOpts))}>
            <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', textAlign: 'left' }}>
              <SchoolIcon color="primary" sx={{ fontSize: 36, mt: 0.25 }} aria-hidden />
              <Box>
                {titleRow('professional', 'Professional / SME')}
                <Typography variant="body2" color="text.secondary">
                  Establish a public profile to gain professional recognition and contribute
                  expertise (e.g., medical, legal) to the knowledge base.
                </Typography>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      ) : null}
    </Stack>
  );
}
